import * as React from 'react';
import {connect} from "react-redux";
import {Customer} from "../../models/Customer";
import {ApplicationState} from "../../store";
import { ColumnData } from '../../models/ColumnData';
import {Tab, TabTypes} from '../Tab';
import {t} from "../../core/localization";
import {CustomerFinancesList} from "../components/CustomerFinancesList";
import {Invoice} from "../../models/Invoice";
import {Payment} from "../../models/Payment";
import {actionDeleteInvoices, actionInvoiceDelete, actionInvoiceFetchByCustomer} from "../../store/invoices";
import {actionDeletePayments, actionPaymentDelete, actionPaymentFetchByCustomer} from "../../store/payments";
import {CustomerFinances, FinanceType} from "../../models/CustomerFinances";
import {actionCustomerFinances, actionCustomerFinancesDelete} from "../../store/customers";
import CustomersService from "../../services/CustomersService";
import {TabsService} from "../TabsService";
import {PaymentsTabProps} from "../payments/PaymentsTab";

const enum CLICK_ACTION_TYPE {
	OPEN,
	COPY,
	DELETE,
	DELETE_MULTIPLE
}

export interface CustomerFinancesTabProps {
	tab: Tab
	item: any
	invoices: Invoice[]
	payments: Payment[]
	finances: CustomerFinances[]
}

export interface CustomerFinancesTabState {
}

function mapStateToProps(state: ApplicationState) {
	return {
		tab: state.tabs.active,
		item: state.tabs.active.item,
		invoices: state.invoices.customerList,
		payments: state.payments.customerList,
		finances: state.customers.finances
	};
}

class CustomerFinancesTab extends React.Component<CustomerFinancesTabProps, CustomerFinancesTabState> {

	constructor(props: CustomerFinancesTabProps) {
		super(props)
	}

	componentDidMount() {

		if(this.props && this.props.item) {

			let {item, invoices, payments} = this.props;

			this.loadData(item);
			let finances = CustomersService.setFinances(invoices, payments);
			actionCustomerFinances(finances);
		}
	}

	componentDidUpdate(prevProps: Readonly<CustomerFinancesTabProps>, prevState: Readonly<CustomerFinancesTabState>, snapshot?: any): void {

		if(prevProps.item.id !== this.props.item.id) {
			this.loadData(this.props.item);
		}

		if(prevProps.invoices.length !== this.props.invoices.length || prevProps.payments.length !== this.props.payments.length) {

			let {invoices, payments} = this.props;

			let finances = CustomersService.setFinances(invoices, payments);
			actionCustomerFinances(finances);
		}
	}

	loadData(customer: Customer) {
		actionInvoiceFetchByCustomer(customer);
		actionPaymentFetchByCustomer(customer);
	}

	onOpenClick(item: any) {
		this.createTab(CLICK_ACTION_TYPE.OPEN, item);
	}

	onCopyClick(item: any) {
		this.createTab(CLICK_ACTION_TYPE.COPY, item);
	}

	onDeleteClick(item: any) {

		let newFinances: any = [];
		this.props.finances.map((finance: any) => {
			if(item.id !== finance.id) {
				newFinances.push(finance);
			}
		});

		actionCustomerFinancesDelete(newFinances);

		if(item.type === FinanceType.Invoice) {
			const invoice: Invoice = this.setInvoiceItem(item);
			actionInvoiceDelete(invoice);
		}
		else if(item.type === FinanceType.Payment) {
			const payment: Payment = this.setPaymentItem(item);
			actionPaymentDelete(payment);
		}
	}

	onActionDeleteClick(items: any) {

		if(items && items.length) {

			let removedItemIds: any = [];
			for(let key in items) {
				removedItemIds.push(items[key].id);
			}

			let newFinances: any = [];
			this.props.finances.map((finance: any) => {
				if(removedItemIds.indexOf(finance.id) === -1) {
					newFinances.push(finance);
				}
			});

			actionCustomerFinancesDelete(newFinances);

			const payments: Payment[] = items.filter((item: any) => item.type === FinanceType.Payment);
			const invoices: Invoice[] = items.filter((item: any) => item.type === FinanceType.Invoice);

			if(payments && payments.length) {
				actionDeletePayments(payments);
			}

			if(invoices && invoices.length) {
				actionDeleteInvoices(invoices);
			}
		}
	}

	setInvoiceItem(item: any): Invoice {
		return this.props.invoices.find(invoice => invoice.id === item.id);
	}

	setPaymentItem(item: any): Payment {
		return this.props.payments.find(payment => payment.id === item.id);
	}

	createTab(clickType: any, item: any) {

		if(item) {

			let tabTitle, tabType, newItem = null;

			if(item.type === FinanceType.Invoice) {
				const invoice: Invoice = this.setInvoiceItem(item);
				tabTitle = t.Invoice + ': ' + invoice.number;
				tabType = TabTypes.INVOICES_NEW;
				newItem = clickType === CLICK_ACTION_TYPE.COPY ? newItem = { ...invoice, id: null } : invoice;
			}
			else if(item.type === FinanceType.Payment) {
				const payment: Payment = this.setPaymentItem(item);
				tabTitle = t.Payment + ' ' + payment.description;
				tabType = TabTypes.PAYMENTS_NEW;
				newItem = clickType === CLICK_ACTION_TYPE.COPY ? { ...payment, id: null } : payment
			}

			TabsService.createTab(tabTitle, tabType, newItem);
		}
	}

	renderLegend() {

		const legendStyle:any = {
			wrapper: {
				display: 'flex',
				marginBottom: '7px'
			},
			row: {
				display: 'flex',
				alignItems: 'center',
				width: '75px'
			},
			box1: {
				width: '20px',
				height: '20px',
				backgroundColor: '#f47560'
			},
			box2: {
				width: '20px',
				height: '20px',
				backgroundColor: '#e8c1a0'
			},
			text: {
				margin: '0 0 0 5px',
				fill: 'black',
				fontFamily: 'sans-serif',
				fontSize: '11px',
				dominantBaseline: 'central',
				pointerEvents: 'none',
				userSelect: 'none'
			}
		};

		return(
			<div style={legendStyle.wrapper}>
				<div style={legendStyle.row}><span style={legendStyle.box1} /><p style={legendStyle.text}>{t.Invoices}</p></div>
				<div style={legendStyle.row}><span style={legendStyle.box2} /><p style={legendStyle.text}>{t.Payments}</p></div>
			</div>
		);
	}

	render(): React.ReactNode {

		const finances = this.props.finances;

		const columns = [
			new ColumnData(t.CustomerFinancesInvoiceNumber, "invoiceNumber" ),
			new ColumnData(t.CustomerFinancesInvoiceDate, "date" ),
			new ColumnData(t.CustomerFinancesPrice, "price" )
		];

		return (
			<div style={{padding: '10px'}}>
				{this.renderLegend()}
				<CustomerFinancesList
					columns={columns}
					items={finances}
					title={t.Finances + ': ' + this.props.item.title}
					tab={this.props.tab}
					onOpenClick={item => this.onOpenClick(item)}
					onCopyClick={item => this.onCopyClick(item)}
					onDeleteClick={item => this.onDeleteClick(item)}
					onActionDeleteClick={finances => this.onActionDeleteClick(finances)}
				/>
			</div>
		);
	}
}

export default connect(mapStateToProps)(CustomerFinancesTab);
